<template>
  <div class="preloader" :class="{ 'is-launched': launched }" aria-label="Loading">
    <div class="preloader__scene" aria-hidden="true">
      <div class="preloader__floor"></div>
      <div class="preloader__vignette"></div>
      <div class="preloader__grain"></div>
    </div>
    <div class="preloader__frame" aria-hidden="true">
      <i></i><i></i><i></i><i></i>
    </div>
    <span class="preloader__meta preloader__meta--tl">KETECH® STUDIO</span>
    <span class="preloader__meta preloader__meta--tr">EST. 2016 — IN</span>
    <span class="preloader__meta preloader__meta--br">
      <span :key="metaIdx" class="preloader__meta-item">{{ metaItems[metaIdx] }}</span>
    </span>
    <canvas ref="canvas" class="preloader__canvas"></canvas>
    <p class="preloader__tag">WHERE CODE BECOMES CRAFT</p>
    <template v-if="launched">
      <div class="preloader__brand">
        <img class="preloader__brand-logo" src="../assets/ketech-logo.svg" alt="Ketech logo">
      </div>
      <section
        v-if="!svcMode"
        class="preloader__statement"
        :style="{ animationDelay: manualNav ? '0.7s' : '2.85s' }"
        aria-label="Ketech positioning"
      >
        <span>Software & Web Studio</span>
        <strong>Crafting Ideas<br>Into <mark>Digital</mark></strong>
        <p>Websites, mobile apps, and software built for growing businesses.</p>
        <small>From concept to launch, shaped with design, code, and care.</small>
      </section>
      <p v-if="!svcMode" class="preloader__globe-head">
        <span
          v-for="(wd, i) in headWords"
          :key="i"
          :style="{ animationDelay: (manualNav ? 1 : 3) + i * (manualNav ? 0.15 : 0.4) + 's' }"
        >{{ wd }}</span>
      </p>
      <p class="preloader__copyright">© 2026 KETECH. All rights reserved.</p>
      <template v-if="!svcMode">
      <div
        v-for="(c, i) in statCards"
        v-show="i < revealed"
        :key="i"
        :class="['preloader__statwrap', 'preloader__statwrap--' + i]"
      >
        <span
          class="preloader__statline"
          :style="manualNav ? { animationDelay: 0.6 + i * 0.25 + 's' } : null"
        ></span>
        <div
          v-if="i < revealed"
          class="preloader__statcard"
          :style="manualNav ? { animationDelay: 0.7 + i * 0.25 + 's' } : null"
        >
          <em>STAT · 0{{ i + 1 }}</em>
          <b>{{ statVals[i] }}<i>+</i></b>
          <span>{{ c.label }}</span>
          <span class="preloader__statbar">
            <i :style="{ transform: 'scaleX(' + statVals[i] / c.v + ')' }"></i>
          </span>
        </div>
      </div>
      </template>
      <template v-if="svcMode">
        <section v-if="!contactMode" class="preloader__svc-head" aria-label="Our services">
          <span>What We Do</span>
          <strong>Our<br><mark>Services</mark></strong>
        </section>
        <div v-if="!contactMode" class="preloader__svc-list">
          <div
            v-for="(s, i) in svcItems"
            :key="s.t"
            class="preloader__statcard preloader__svccard"
            :style="{ animationDelay: 1.1 + i * 0.22 + 's' }"
          >
            <em>SRV · 0{{ i + 1 }}</em>
            <b>{{ s.t }}</b>
            <span>{{ s.d }}</span>
            <span class="preloader__statbar"><i style="transform: scaleX(1)"></i></span>
          </div>
        </div>
        <template v-if="contactMode">
          <section class="preloader__svc-head preloader__contact-head" aria-label="Contact Ketech">
            <span>Get In Touch</span>
            <strong>Let's<br><mark>Talk</mark></strong>
            <dl class="preloader__contact-details">
              <div>
                <dt>Phone</dt>
                <dd><a href="tel:+919535454082">+91 9535454082</a></dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd><a href="mailto:ketechservice@gmail.com">ketechservice@gmail.com</a></dd>
              </div>
              <div>
                <dt>Address</dt>
                <dd>1B, Sri Lakshmi Krupa, Main, 3rd Cross Rd, Maruthi Layout, Dasarahalli, Bengaluru, Karnataka 560024</dd>
              </div>
            </dl>
            <div class="preloader__socials">
              <a href="https://wa.me/919535454082?text=Hi%20Ketech!%20I%20visited%20your%20website%20and%20would%20like%20to%20discuss%20a%20project." target="_blank" rel="noopener" aria-label="WhatsApp">WA</a>
              <a href="#" target="_blank" rel="noopener" aria-label="Instagram">IG</a>
              <a href="#" target="_blank" rel="noopener" aria-label="Facebook">FB</a>
              <a href="#" target="_blank" rel="noopener" aria-label="LinkedIn">IN</a>
            </div>
          </section>
          <div class="preloader__contact">
            <form class="preloader__statcard preloader__contactcard" style="animation-delay: 1.1s" @submit.prevent="sendMsg">
              <em>CONTACT · KETECH</em>
              <b>Send A Message</b>
              <input v-model="form.name" type="text" placeholder="Name" required>
              <input v-model="form.email" type="email" placeholder="Email" required>
              <input
                v-model="form.phone"
                type="tel"
                placeholder="Contact Number"
                inputmode="tel"
                pattern="(\+?\d{1,3}[\s-]?)?\d{10}"
                maxlength="15"
                title="Enter a valid 10-digit phone number, e.g. 9535454082 or +91 9535454082"
                required
              >
              <select v-model="form.service" required>
                <option value="" disabled>Select Service</option>
                <option v-for="s in svcItems" :key="s.t" :value="s.t">{{ s.t }}</option>
              </select>
              <textarea v-model="form.req" rows="3" placeholder="Your Requirement" required></textarea>
              <button type="submit">SEND</button>
              <span class="preloader__statbar"><i style="transform: scaleX(1)"></i></span>
            </form>
          </div>
        </template>
      </template>
    </template>
    <div class="preloader__launch-wrap">
      <button
        class="preloader__launch"
        :class="{ 'is-on': launched }"
        type="button"
        aria-label="Launch website"
        @click="launch"
      >
        <span class="preloader__opt preloader__opt--on">ON</span>
        <span class="preloader__opt preloader__opt--off">ON</span>
        <span class="preloader__knob"></span>
      </button>
      <span class="preloader__launch-label">{{ launched ? 'LAUNCHING…' : 'LAUNCH WEBSITE' }}</span>
    </div>
    <p class="preloader__hint">{{ isTouch ? 'touch the letters' : 'move your cursor through the letters' }}</p>
  </div>
</template>

<script>
const GLYPHS = '01</>{}[]()=+*#$;:._-|&^%!?~'

export default {
  name: 'HomePage',
  data() {
    return {
      raf: null,
      cleanup: null,
      isTouch: typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
      launched: false,
      metaItems: ['12.97°N · 77.59°E', 'BLR · INDIA', 'UTC +05:30', 'SYSTEMS NOMINAL'],
      metaIdx: 0,
      metaTimer: null,
      statCards: [
        { v: 9, label: 'Years Experience' },
        { v: 40, label: 'Corporate Clients' },
        { v: 90, label: 'Systems Delivered' }
      ],
      headWords: ['CORPORATE', 'SOFTWARE', '—', 'CRAFTED', 'IN', 'INDIA'],
      revealed: 0,
      statVals: [0, 0, 0],
      statTimer: null,
      svcMode: false,
      contactMode: false,
      manualNav: false,
      form: { name: '', email: '', phone: '', service: '', req: '' },
      svcItems: [
        { t: 'Web Development', d: 'Fast, modern websites that convert' },
        { t: '3D Websites', d: 'Immersive WebGL experiences' },
        { t: 'Software Development', d: 'Custom systems built to scale' },
        { t: 'Mobile App Development', d: 'iOS & Android, end to end' },
        { t: 'Hosting', d: 'Reliable, secure cloud hosting' },
        { t: 'UI / UX Design', d: 'Interfaces users love to use' },
        { t: 'ERP / CRM Solution', d: 'Business tools that fit your flow' },
        { t: 'Domain Purchase', d: 'Find and secure your name' }
      ]
    }
  },
  computed: {
    scene() {
      return this.contactMode ? 'contact' : this.svcMode ? 'services' : 'hero'
    }
  },
  methods: {
    goScene(s) {
      // manual navigation takes over from the timed intro sequence
      this.manualNav = true
      window.clearTimeout(this.statTimer)
      this.revealed = this.statCards.length
      this.statVals = this.statCards.map((c) => c.v)
      this.svcMode = s !== 'hero'
      this.contactMode = s === 'contact'
      if (this._blip) this._blip(700, 0.05, 0.03, 'triangle')
    },
    sendMsg() {
      // no backend yet: hand the enquiry over as a WhatsApp chat
      const f = this.form
      const msg =
        `Hi Ketech! New project enquiry:\n\n` +
        `Name: ${f.name}\nEmail: ${f.email}\nPhone: ${f.phone}\n` +
        `Service: ${f.service}\n\nRequirement:\n${f.req}`
      window.open('https://wa.me/919535454082?text=' + encodeURIComponent(msg), '_blank', 'noopener')
    },
    launch() {
      if (this.launched) return
      this.launched = true
      // remember the launch so a refresh goes straight to the globe scene
      try { localStorage.setItem('ketech-launched', '1') } catch (e) { /* private mode */ }
      if (this._blip) {
        // soft mechanical click, then a power-up sweep as the globe ignites
        this._blip(1200, 0.02, 0.04, 'triangle')
        window.setTimeout(() => this._blip(220, 0.45, 0.045, 'sine', 990), 140)
      }
      // reveal cards one at a time: card in → count up → pause → next card
      const CARD_IN = 620 // matches cardIn animation before counting starts
      const COUNT = 1100
      const PAUSE = 900 // beat after the number lands before the next card

      const countTo = (i, done) => {
        const v = this.statCards[i].v
        const t0 = performance.now()
        const tick = (now) => {
          const t = Math.min(1, (now - t0) / COUNT)
          this.statVals[i] = Math.round(v * (1 - Math.pow(1 - t, 3)))
          if (t < 1) requestAnimationFrame(tick)
          else if (done) done()
        }
        requestAnimationFrame(tick)
      }

      const revealNext = () => {
        if (this.manualNav) return
        const i = this.revealed
        if (i >= this.statCards.length) {
          // stats done: hold a beat, then wipe the hero into the services scene
          this.statTimer = window.setTimeout(() => {
            this.svcMode = true
            if (this._blip) this._blip(320, 0.5, 0.045, 'sine', 1400)
            // after the services have had their moment, slide into contact
            this.statTimer = window.setTimeout(() => {
              this.contactMode = true
              if (this._blip) this._blip(420, 0.45, 0.04, 'sine', 1600)
            }, 12000)
          }, 1600)
          return
        }
        this.revealed++
        this.statTimer = window.setTimeout(() => {
          countTo(i, () => {
            this.statTimer = window.setTimeout(revealNext, PAUSE)
          })
        }, CARD_IN)
        if (this._blip) this._blip(880 + i * 140, 0.05, 0.03, 'triangle')
      }

      window.setTimeout(revealNext, 2600)
    }
  },
  mounted() {
    const canvas = this.$refs.canvas
    const ctx = canvas.getContext('2d')
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const mouse = { x: -9999, y: -9999 }
    const rand = (a) => a[Math.floor(Math.random() * a.length)]
    let glyphs = []
    let frame = 0
    let cellGap = 14
    let launchAt = 0
    // reversible morph progress (frames): eases toward the active scene
    let svcP = 0
    let ctP = 0

    // dot-matrix robot head the globe collapses into for the services scene
    // strokes are 2 dots thick like the wordmark so it reads crisply
    // '#' head (white), 'E' eyes (gold, blink), 'M' smile (gold), 'A' antenna tips
    const FACE = [
      '...........AA...........',
      '...........##...........',
      '......############......',
      '....################....',
      '...##..............##...',
      '..##................##..',
      '..##..EEE......EEE..##..',
      '..##.EEEEE....EEEEE.##..',
      '..##.EEEEE....EEEEE.##..',
      '..##..EEE......EEE..##..',
      '..##................##..',
      '..##..M..........M..##..',
      '..##..MM........MM..##..',
      '..##...MM......MM...##..',
      '..##....MMMMMMMM....##..',
      '..##................##..',
      '...##..............##...',
      '....################....',
      '......############......'
    ]
    const FACE_ROWS = FACE.length
    const FACE_COLS = FACE[0].length
    const facePts = []
    FACE.forEach((row, r) => {
      for (let c = 0; c < row.length; c++) {
        if (row[c] === '.') continue
        facePts.push({ r, c, eye: row[c] === 'E', gold: row[c] !== '#' })
      }
    })

    // chat bubble the robot folds into for the contact scene
    // '#' bubble (white), 'F' typing dots (gold, pulse in a wave)
    const ENV = [
      '......##############......',
      '....##################....',
      '...##................##...',
      '..##..................##..',
      '..##..................##..',
      '..##...FF...FF...FF...##..',
      '..##...FF...FF...FF...##..',
      '..##..................##..',
      '..##..................##..',
      '...##................##...',
      '....##################....',
      '......##############......',
      '.......###................',
      '......###.................',
      '.....###..................'
    ]
    const ENV_ROWS = ENV.length
    const ENV_COLS = ENV[0].length
    const envPts = []
    ENV.forEach((row, r) => {
      for (let c = 0; c < row.length; c++) {
        if (row[c] === '.') continue
        envPts.push({ r, c, gold: row[c] === 'F' })
      }
    })

    // tiny synth for UI blips (created lazily; silent until browser allows audio)
    let actx = null
    const blip = (freq = 800, dur = 0.05, vol = 0.035, type = 'square', glide = 0) => {
      try {
        actx = actx || new (window.AudioContext || window.webkitAudioContext)()
        if (actx.state === 'suspended') actx.resume()
        const o = actx.createOscillator()
        const g = actx.createGain()
        o.type = type
        o.frequency.value = freq
        if (glide) o.frequency.exponentialRampToValueAtTime(glide, actx.currentTime + dur)
        g.gain.setValueAtTime(vol, actx.currentTime)
        g.gain.exponentialRampToValueAtTime(0.0001, actx.currentTime + dur)
        o.connect(g)
        g.connect(actx.destination)
        o.start()
        o.stop(actx.currentTime + dur)
      } catch (e) { /* audio unavailable */ }
    }
    this._blip = blip

    const build = () => {
      const w = window.innerWidth
      const h = window.innerHeight
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      // hand-drawn dot-matrix font: every stroke is exactly 2 dots wide,
      // identical structure on every screen size
      const FONT = {
        K: ['##.....##', '##....##.', '##...##..', '##..##...', '##.##....', '####.....', '####.....', '##.##....', '##..##...', '##...##..', '##....##.', '##.....##'],
        E: ['########', '########', '##......', '##......', '##......', '#######.', '#######.', '##......', '##......', '##......', '########', '########'],
        T: ['########', '########', '...##...', '...##...', '...##...', '...##...', '...##...', '...##...', '...##...', '...##...', '...##...', '...##...'],
        C: ['..#####..', '.#######.', '##.....##', '##.......', '##.......', '##.......', '##.......', '##.......', '##.......', '##.....##', '.#######.', '..#####..'],
        H: ['##.....##', '##.....##', '##.....##', '##.....##', '##.....##', '#########', '#########', '##.....##', '##.....##', '##.....##', '##.....##', '##.....##']
      }
      const WORD = 'KETECH'
      const SP = 2
      const rows = 12
      const totalCols =
        WORD.split('').reduce((n, c) => n + FONT[c][0].length, 0) + SP * (WORD.length - 1)

      const cell = Math.min((w * (w < 720 ? 0.68 : 0.48)) / totalCols, 11)
      cellGap = cell
      const x0 = w / 2 - (totalCols * cell) / 2
      const y0 = h * (w < 720 ? 0.47 : 0.44) - (rows * cell) / 2

      glyphs = []
      let colOff = 0
      let li = 0
      for (const letter of WORD) {
        const map = FONT[letter]
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < map[r].length; c++) {
            if (map[r][c] === '#') {
              const tx = x0 + (colOff + c) * cell
              const ty = y0 + r * cell
              glyphs.push({
                x: tx,
                y: ty,
                tx,
                ty,
                vx: 0,
                vy: 0,
                ch: rand(GLYPHS),
                gold: Math.random() < 0.18,
                born: false,
                bornAt: 0,
                // each letter fills top-to-bottom, cascading across the word
                delay: li * 9 + r * 3.5 + Math.random() * 3
              })
            }
          }
        }
        colOff += map[0].length + SP
        li++
      }

      // pre-assign each dot a point on a fibonacci sphere for the globe morph
      const n = glyphs.length
      glyphs.forEach((p, i) => {
        const sy = 1 - (2 * (i + 0.5)) / n
        const sr = Math.sqrt(1 - sy * sy)
        const phi = i * 2.399963
        p.sx = sr * Math.cos(phi)
        p.sy = sy
        p.sz = sr * Math.sin(phi)
        // and cells on the robot face / envelope for the later morphs
        p.fi = i % facePts.length
        p.ei = i % envPts.length
      })

      frame = 0
    }

    const gap = () => cellGap

    const step = () => {
      const w = window.innerWidth
      const h = window.innerHeight
      const g = gap()
      frame++
      ctx.clearRect(0, 0, w, h)

      // scene morphs advance or rewind depending on the active scene
      svcP = Math.max(0, Math.min(70, svcP + (this.svcMode ? 1 : -1)))
      ctP = Math.max(0, Math.min(70, ctP + (this.contactMode ? 1 : -1)))

      ctx.font = `${g + 1}px Menlo, Consolas, monospace`

      let bornNow = 0
      let disturbed = 0

      // wordmark glyphs rain down into place
      for (const p of glyphs) {
        if (!p.born) {
          if (frame > p.delay || this.launched) {
            p.born = true
            p.bornAt = frame
            bornNow++
          } else continue
        }
        if (this.launched) {
          // gentle staggered morph from the wordmark into a slow-rotating globe
          if (!launchAt) launchAt = frame
          const t = Math.min(1, Math.max(0, (frame - launchAt - p.delay * 0.15) / 50))
          const e = t * t * (3 - 2 * t)
          const rot = (frame - launchAt) * 0.005
          const X = p.sx * Math.cos(rot) + p.sz * Math.sin(rot)
          const Z = -p.sx * Math.sin(rot) + p.sz * Math.cos(rot)
          const R = Math.min(w, h) * (w < 720 ? 0.2 : 0.24)
          const gx = w / 2 + X * R
          const gy = h * (w < 720 ? 0.48 : 0.45) + p.sy * R * 0.92
          let tx = p.tx + (gx - p.tx) * e
          let ty = p.ty + (gy - p.ty) * e
          let fe = 0
          let fp = null
          let fr = 0
          let ce = 0
          let ep = null
          if (svcP > 0) {
            // globe dots peel off and reassemble as the studio robot
            const ft = Math.min(1, Math.max(0, (svcP - p.delay * 0.12) / 55))
            fe = ft * ft * (3 - 2 * ft)
            fp = facePts[p.fi]
            const cellF = (Math.min(w, h) * 0.36) / FACE_ROWS
            const fx0 = w * (w < 720 ? 0.5 : 0.42) - (FACE_COLS * cellF) / 2
            const fy0 = h * (w < 720 ? 0.37 : 0.5) - (FACE_ROWS * cellF) / 2
            const bob = Math.sin(frame * 0.02) * 5
            let fx = fx0 + (fp.c + 0.5) * cellF
            let fy = fy0 + (fp.r + 0.5) * cellF + bob
            let blink = 1
            if (fp.eye) {
              // periodic blink: eye dots squash onto the eyelid line
              const bt = frame % 190
              if (bt > 170) blink = Math.abs(Math.cos(((bt - 170) / 20) * Math.PI))
              const eyeCy = fy0 + 8 * cellF + bob
              fy = eyeCy + (fy - eyeCy) * blink
            }
            fr = cellGap * (fp.eye ? 0.36 * (0.3 + 0.7 * blink) : 0.3)
            if (ctP > 0) {
              // the robot folds itself into a chat bubble
              const ct = Math.min(1, Math.max(0, (ctP - p.delay * 0.12) / 55))
              ce = ct * ct * (3 - 2 * ct)
              ep = envPts[p.ei]
              const cellE = (Math.min(w, h) * (w < 720 ? 0.18 : 0.3)) / ENV_ROWS
              const ex0 = w * (w < 720 ? 0.5 : 0.42) - (ENV_COLS * cellE) / 2
              const ey0 = h * (w < 720 ? 0.52 : 0.5) - (ENV_ROWS * cellE) / 2
              const ex = ex0 + (ep.c + 0.5) * cellE
              const ey = ey0 + (ep.r + 0.5) * cellE + bob
              fx += (ex - fx) * ce
              fy += (ey - fy) * ce
              fr += (cellGap * 0.3 - fr) * ce
            }
            tx += (fx - tx) * fe
            ty += (fy - ty) * fe
          }
          p.vx += (tx - p.x) * 0.04
          p.vy += (ty - p.y) * 0.04
          p.vx *= 0.85
          p.vy *= 0.85
          p.x += p.vx
          p.y += p.vy
          const depth = (Z + 1) / 2
          let a = 1 + (0.15 + 0.85 * depth - 1) * e
          let rad = cellGap * 0.3 * (1 + (0.5 + 0.7 * depth - 1) * e)
          if (fe > 0) {
            // settle to steady brightness; eyes squash while blinking
            a += (0.95 - a) * fe
            rad += (fr - rad) * fe
            // typing dots pulse in a left-to-right wave
            if (ce > 0.5 && ep.gold) a *= 0.45 + 0.55 * Math.abs(Math.sin(frame * 0.08 - ep.c * 0.35))
          }
          const gold = ce > 0.5 && ep ? ep.gold : fe > 0.5 && fp ? fp.gold : p.gold
          ctx.fillStyle = gold
            ? `rgba(245, 197, 24, ${a})`
            : `rgba(255, 255, 255, ${a * 0.9})`
          ctx.beginPath()
          ctx.arc(p.x, p.y, rad, 0, 6.284)
          ctx.fill()
          continue
        }

        const dx = p.tx - p.x
        const dy = p.ty - p.y
        p.vx += dx * 0.04
        p.vy += dy * 0.04
        const mx = p.x - mouse.x
        const my = p.y - mouse.y
        const d2 = mx * mx + my * my
        if (d2 < 9000) {
          const f = (9000 - d2) / 9000
          const d = Math.sqrt(d2) || 1
          p.vx += (mx / d) * f * 8
          p.vy += (my / d) * f * 8
          p.ch = rand(GLYPHS)
          disturbed++
        }
        p.vx *= 0.85
        p.vy *= 0.85
        p.x += p.vx
        p.y += p.vy
        const moving = Math.abs(p.vx) + Math.abs(p.vy) > 1.2
        const fresh = frame - p.bornAt < 24
        if (fresh) {
          // newly written: flickers as code before settling into a dot
          if (Math.random() < 0.4) p.ch = rand(GLYPHS)
          ctx.fillStyle = 'rgba(245, 197, 24, 0.9)'
          ctx.fillText(p.ch, p.x, p.y)
        } else if (moving) {
          // scattered dots show raw code characters
          if (Math.random() < 0.3) p.ch = rand(GLYPHS)
          ctx.fillStyle = 'rgba(255, 246, 200, 0.95)'
          ctx.fillText(p.ch, p.x, p.y)
        } else {
          // settled: clean dot-matrix look
          ctx.fillStyle = p.gold ? '#f5c518' : 'rgba(255, 255, 255, 0.88)'
          ctx.beginPath()
          ctx.arc(p.x, p.y, g * 0.3, 0, 6.284)
          ctx.fill()
        }
      }

      // typing ticks while the word writes itself, rising in pitch
      if (bornNow && frame % 3 === 0) {
        blip(420 + frame * 4, 0.035, 0.03, 'square')
      }
      // soft granular crackle while the cursor scatters dots
      if (disturbed && frame % 7 === 0) {
        blip(220 + Math.random() * 180, 0.05, 0.025, 'triangle')
      }

      this.raf = requestAnimationFrame(step)
    }

    const onMove = (e) => {
      const t = e.touches ? e.touches[0] : e
      mouse.x = t.clientX
      mouse.y = t.clientY
    }
    const onLeave = () => {
      mouse.x = -9999
      mouse.y = -9999
    }

    // scroll / swipe between hero → services → contact
    const order = ['hero', 'services', 'contact']
    let lastNav = 0
    const navBy = (dir) => {
      const now = Date.now()
      if (!this.launched || now - lastNav < 900) return
      const next = order.indexOf(this.scene) + dir
      if (next < 0 || next >= order.length) return
      lastNav = now
      this.goScene(order[next])
    }
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > 30) navBy(e.deltaY > 0 ? 1 : -1)
    }
    let swipeY = null
    const onTouchStart = (e) => {
      // touches inside scrollable panels scroll them, not the scenes
      if (e.target.closest && e.target.closest('.preloader__contact')) {
        swipeY = null
        return
      }
      swipeY = e.touches[0].clientY
    }
    const onTouchMoveNav = (e) => {
      // navigate as soon as the swipe clears the threshold — touchend can be
      // swallowed by the browser's pull-to-refresh gesture
      if (swipeY === null) return
      const d = swipeY - e.touches[0].clientY
      if (Math.abs(d) > 50) {
        navBy(d > 0 ? 1 : -1)
        swipeY = null
      }
    }
    const onTouchEnd = () => { swipeY = null }
    window.addEventListener('wheel', onWheel, { passive: true })
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove', onTouchMoveNav, { passive: true })
    window.addEventListener('touchend', onTouchEnd, { passive: true })

    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('pointerleave', onLeave)
    // on touch devices release the repel point, otherwise dots stay stuck
    window.addEventListener('touchend', onLeave)
    window.addEventListener('touchcancel', onLeave)
    window.addEventListener('pointerup', onLeave)
    window.addEventListener('resize', build)

    build()
    step()

    // returning visitor: skip the launch switch, start from the globe animation
    let seen = false
    try { seen = localStorage.getItem('ketech-launched') === '1' } catch (e) { /* private mode */ }
    if (seen) this.launch()

    this.metaTimer = window.setInterval(() => {
      this.metaIdx = (this.metaIdx + 1) % this.metaItems.length
    }, 3200)

    this.cleanup = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('pointerleave', onLeave)
      window.removeEventListener('resize', build)
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMoveNav)
      window.removeEventListener('touchend', onTouchEnd)
    }
  },
  beforeUnmount() {
    if (this.raf) cancelAnimationFrame(this.raf)
    if (this.metaTimer) window.clearInterval(this.metaTimer)
    if (this.statTimer) window.clearTimeout(this.statTimer)
    if (this.cleanup) this.cleanup()
  }
}
</script>

<style scoped>
.preloader {
  position: fixed;
  inset: 0;
  min-height: 100dvh;
  background: radial-gradient(90% 90% at 50% 40%, #121110 0%, #060606 70%);
  overflow: hidden;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  /* keep vertical swipes as scene navigation, not browser scroll/refresh */
  touch-action: none;
  overscroll-behavior: none;
}

.preloader__canvas {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
}

/* cinematic 3D scene behind the wordmark */
.preloader__scene {
  position: absolute;
  inset: 0;
  overflow: hidden;
  perspective: 600px;
}

.preloader__floor {
  position: absolute;
  left: -60%;
  right: -60%;
  bottom: -12%;
  height: 55%;
  transform: rotateX(72deg);
  transform-origin: top;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(180deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 90px 90px;
  mask-image: linear-gradient(180deg, transparent 0%, #000 30%, #000 100%);
  -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 30%, #000 100%);
  animation: floorGlide 5s linear infinite;
}

.preloader__vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(75% 75% at 50% 45%, transparent 55%, rgba(0, 0, 0, 0.8) 100%);
}

@keyframes floorGlide {
  to { background-position: 0 90px, 0 90px; }
}

.preloader__grain {
  position: absolute;
  inset: 0;
  opacity: 0.05;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E");
}

.preloader__frame {
  position: absolute;
  inset: 26px;
  z-index: 2;
  pointer-events: none;
}

.preloader__frame i {
  position: absolute;
  width: 16px;
  height: 16px;
  border: 1px solid rgba(255, 255, 255, 0.28);
}

.preloader__frame i:nth-child(1) { top: 0; left: 0; border-width: 1px 0 0 1px; }
.preloader__frame i:nth-child(2) { top: 0; right: 0; border-width: 1px 1px 0 0; }
.preloader__frame i:nth-child(3) { bottom: 0; left: 0; border-width: 0 0 1px 1px; }
.preloader__frame i:nth-child(4) { bottom: 0; right: 0; border-width: 0 1px 1px 0; }

.preloader__meta {
  position: absolute;
  z-index: 2;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.22em;
  color: rgba(255, 255, 255, 0.34);
  opacity: 0;
  animation: fadeIn 1.2s ease forwards 1.2s;
}

.preloader__meta--tl { top: 34px; left: 40px; }
.preloader__meta--tr { top: 34px; right: 40px; }
.preloader__meta--br {
  bottom: 34px;
  right: 40px;
  min-width: 150px;
  text-align: right;
  color: rgba(245, 197, 24, 0.5);
}

.preloader__meta-item {
  display: inline-block;
  animation: metaCycle 3.2s ease both;
}

@keyframes metaCycle {
  0% { opacity: 0; transform: translateY(6px); }
  12%, 82% { opacity: 1; transform: translateY(0); }
  94%, 100% { opacity: 0; transform: translateY(-6px); }
}

.preloader__tag {
  position: absolute;
  left: 50%;
  top: calc(50% + 96px);
  transform: translateX(-50%);
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.42em;
  white-space: nowrap;
  color: #f5c518;
  opacity: 0;
  animation: fadeIn 1s ease forwards 2s;
}

.preloader__tag::before {
  content: "";
  position: absolute;
  top: -28px;
  left: 50%;
  width: 44px;
  height: 1px;
  transform: translateX(-50%);
  background: linear-gradient(90deg, transparent, rgba(245, 197, 24, 0.7), transparent);
}

.preloader__launch-wrap {
  position: absolute;
  left: 50%;
  top: calc(50% + 158px);
  z-index: 3;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  opacity: 0;
  animation: fadeIn 1s ease forwards 2.6s;
}

.preloader__launch {
  position: relative;
  width: 104px;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  background: linear-gradient(180deg, #191919, #0c0c0c);
  box-shadow:
    inset 0 3px 8px rgba(0, 0, 0, 0.95),
    inset 0 -1px 0 rgba(255, 255, 255, 0.05),
    0 1px 0 rgba(255, 255, 255, 0.05),
    0 12px 30px rgba(0, 0, 0, 0.6),
    0 0 18px rgba(255, 77, 46, 0.12);
  cursor: pointer;
  transition: box-shadow 0.4s ease;
}

/* attention cue: soft ripple glow until the user flips it */
.preloader__launch:not(.is-on)::before,
.preloader__launch:not(.is-on)::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 10px;
  pointer-events: none;
  animation: launchRipple 3s ease-out infinite;
}

.preloader__launch:not(.is-on)::after {
  animation-delay: 1.5s;
}

.preloader__launch.is-on {
  box-shadow:
    inset 0 3px 8px rgba(0, 0, 0, 0.95),
    inset 0 -1px 0 rgba(255, 255, 255, 0.05),
    0 1px 0 rgba(255, 255, 255, 0.05),
    0 12px 30px rgba(0, 0, 0, 0.6),
    0 0 30px rgba(245, 197, 24, 0.35);
}

.preloader__opt {
  position: absolute;
  top: 0;
  width: 50%;
  height: 100%;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  transition: color 0.35s ease, text-shadow 0.35s ease;
}

.preloader__opt--on {
  left: 0;
  color: rgba(255, 255, 255, 0.12);
}

.preloader__opt--off {
  right: 0;
  color: #34c759;
  text-shadow: 0 0 14px rgba(52, 199, 89, 0.7);
}

.preloader__launch.is-on .preloader__opt--on {
  color: #f5c518;
  text-shadow: 0 0 14px rgba(245, 197, 24, 0.8);
}

.preloader__launch.is-on .preloader__opt--off {
  color: rgba(255, 255, 255, 0.12);
  text-shadow: none;
}

.preloader__knob {
  position: absolute;
  top: -4px;
  left: -3px;
  width: 54%;
  height: calc(100% + 8px);
  border-radius: 9px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.09), transparent 40%),
    linear-gradient(180deg, #2b2b2b, #131313);
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-bottom-color: rgba(0, 0, 0, 0.8);
  box-shadow:
    0 8px 18px rgba(0, 0, 0, 0.75),
    0 1px 2px rgba(0, 0, 0, 0.9),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transition:
    left 0.35s cubic-bezier(0.34, 1.4, 0.64, 1),
    top 0.1s ease,
    height 0.1s ease;
}

/* machined grip ridges */
.preloader__knob::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 14px;
  height: 10px;
  transform: translate(-50%, -50%);
  background: repeating-linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.16) 0 1px,
    transparent 1px 5px
  );
}

.preloader__launch:active .preloader__knob {
  top: -2px;
  height: calc(100% + 4px);
}

.preloader__launch.is-on .preloader__knob {
  left: calc(50% - 3px);
}

.preloader__launch-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.25s ease;
  animation: labelBreath 2.2s ease-in-out infinite;
}

@keyframes launchRipple {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 197, 24, 0.28);
  }
  70%, 100% {
    box-shadow: 0 0 26px 18px rgba(245, 197, 24, 0);
  }
}

@keyframes labelBreath {
  0%, 100% { color: rgba(255, 255, 255, 0.6); }
  50% { color: #f5c518; }
}

.preloader__launch-wrap:hover .preloader__launch-label,
.preloader__launch.is-on ~ .preloader__launch-label {
  color: #f5c518;
}

.preloader__hint {
  position: absolute;
  left: 50%;
  bottom: 30px;
  transform: translateX(-50%);
  margin: 0;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
  opacity: 0;
  animation: fadeIn 1s ease forwards 3s;
}

@keyframes fadeIn {
  to { opacity: 1; }
}

.preloader__globe-head {
  position: absolute;
  left: 50%;
  bottom: 9%;
  z-index: 3;
  transform: translateX(-50%);
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4em;
  white-space: nowrap;
  color: rgba(245, 197, 24, 0.85);
}

.preloader__brand {
  position: absolute;
  left: 50%;
  top: 48px;
  z-index: 3;
  width: min(9vw, 130px);
  transform: translateX(-50%) translateY(8px);
  text-align: center;
  opacity: 0;
  animation: brandIn 0.7s ease 2.6s forwards;
}

.preloader__brand-logo {
  display: block;
  width: 100%;
  height: auto;
  filter:
    brightness(1.08)
    drop-shadow(0 0 18px rgba(245, 197, 24, 0.12))
    drop-shadow(0 10px 30px rgba(0, 0, 0, 0.45));
}

@keyframes brandIn {
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.preloader__copyright {
  position: absolute;
  left: 50%;
  bottom: 34px;
  z-index: 3;
  margin: 0;
  transform: translateX(-50%);
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.42);
  opacity: 0;
  animation: fadeIn 0.75s ease 3.2s forwards;
}

.preloader__statement {
  position: absolute;
  left: 5%;
  top: 18%;
  z-index: 3;
  width: min(28vw, 390px);
  margin: 0;
  color: #f7f7f2;
  opacity: 0;
  transform: translateY(14px);
  animation: statementIn 0.75s ease 2.85s forwards;
}

.preloader__statement span {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 8px 14px 7px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.95);
  color: #17120f;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: clamp(8px, 0.72vw, 10px);
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: normal;
  clip-path: polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%);
}

.preloader__statement strong {
  display: block;
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: clamp(34px, 4.1vw, 66px);
  font-weight: 900;
  line-height: 0.96;
  letter-spacing: 0;
  text-transform: uppercase;
  text-shadow:
    0 0 18px rgba(255, 255, 255, 0.06),
    3px 3px 0 rgba(245, 197, 24, 0.2);
}

.preloader__statement mark {
  background: none;
  color: #f5c518;
  text-shadow:
    0 0 20px rgba(245, 197, 24, 0.22),
    3px 3px 0 rgba(245, 197, 24, 0.32);
}

.preloader__statement p {
  max-width: 330px;
  margin: 14px 0 0;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.55;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.62);
}

.preloader__statement small {
  display: block;
  max-width: 300px;
  margin-top: 12px;
  padding-left: 12px;
  border-left: 2px solid rgba(245, 197, 24, 0.75);
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 8px;
  font-weight: 700;
  line-height: 1.6;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(245, 197, 24, 0.78);
}

@keyframes statementIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.preloader__globe-head span {
  display: inline-block;
  margin-right: 0.9em;
  opacity: 0;
  transform: translateY(8px);
  animation: wordIn 0.6s ease forwards;
}

.preloader__globe-head span:last-child {
  margin-right: 0;
}

@keyframes wordIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* stat cards tethered to the globe at three points */
.preloader__statwrap {
  position: absolute;
  z-index: 3;
  display: flex;
  align-items: center;
}

.preloader__statwrap--0 {
  left: calc(50% + 19vmin);
  top: 30%;
}

.preloader__statwrap--1 {
  right: calc(50% + 19vmin);
  top: 54%;
  flex-direction: row-reverse;
}

.preloader__statwrap--2 {
  left: calc(50% + 15vmin);
  top: 64%;
}

/* L-shaped elbow connector: horizontal out from globe, then bends into card */
.preloader__statline {
  position: relative;
  align-self: flex-start;
  margin-top: 26px;
  width: 96px;
  height: 40px;
  border-top: 1px solid rgba(245, 197, 24, 0.55);
  border-right: 1px solid rgba(245, 197, 24, 0.55);
  opacity: 0;
  animation: lineDraw 0.5s ease 0.15s forwards;
}

.preloader__statwrap--1 .preloader__statline {
  border-right: none;
  border-left: 1px solid rgba(245, 197, 24, 0.55);
}

.preloader__statwrap--2 .preloader__statline {
  margin-top: 62px;
  height: 0;
}

.preloader__statwrap--2 .preloader__statline::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 100%;
  width: 1px;
  height: 112px;
  background: rgba(245, 197, 24, 0.55);
}

.preloader__statwrap--2 .preloader__statline::before {
  top: -116px;
}

/* anchor dot at the globe end of the elbow */
.preloader__statline::before {
  content: "";
  position: absolute;
  left: -4px;
  top: -4px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f5c518;
  box-shadow: 0 0 12px rgba(245, 197, 24, 0.9);
  animation: dotPulse 2.4s ease-in-out infinite;
}

.preloader__statwrap--1 .preloader__statline::before {
  left: auto;
  right: -4px;
}

@keyframes lineDraw {
  to { opacity: 1; }
}

@keyframes dotPulse {
  0%, 100% { box-shadow: 0 0 8px rgba(245, 197, 24, 0.7); }
  50% { box-shadow: 0 0 16px 3px rgba(245, 197, 24, 0.9); }
}

.preloader__statcard {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 18px 26px 16px;
  background:
    linear-gradient(135deg, rgba(245, 197, 24, 0.09), transparent 45%),
    rgba(15, 14, 12, 0.78);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  /* HUD panel: notched corners */
  clip-path: polygon(
    16px 0, 100% 0, 100% calc(100% - 16px),
    calc(100% - 16px) 100%, 0 100%, 0 16px
  );
  box-shadow:
    inset 0 0 0 1px rgba(245, 197, 24, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    inset 0 0 30px rgba(245, 197, 24, 0.05),
    0 24px 60px rgba(0, 0, 0, 0.6),
    0 0 40px rgba(245, 197, 24, 0.06);
  overflow: hidden;
  animation: cardIn 0.55s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

/* gold corner blade on the notch */
.preloader__statcard::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #f5c518 8px, transparent 8px);
  filter: drop-shadow(0 0 6px rgba(245, 197, 24, 0.6));
}

/* reveal shine sweep */
.preloader__statcard::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 30%, rgba(245, 197, 24, 0.18) 50%, transparent 70%);
  transform: translateX(-110%);
  animation: cardShine 0.9s ease-out 0.25s forwards;
}

.preloader__statcard em {
  font-style: normal;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: rgba(245, 197, 24, 0.6);
}

.preloader__statcard b {
  display: inline-block;
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: 38px;
  font-weight: 900;
  line-height: 0.88;
  letter-spacing: 0.02em;
  color: #f7f7f7;
  font-variant-numeric: tabular-nums;
  text-transform: uppercase;
  transform: skewX(-10deg);
  text-shadow:
    0 0 10px rgba(255, 255, 255, 0.08),
    2px 2px 0 rgba(245, 197, 24, 0.22),
    4px 4px 0 rgba(0, 0, 0, 0.35);
}

.preloader__statcard b i {
  font-style: normal;
  font-size: 22px;
  color: #f5c518;
  text-shadow:
    0 0 10px rgba(245, 197, 24, 0.22),
    2px 2px 0 rgba(0, 0, 0, 0.3);
}

.preloader__statcard span {
  margin-top: 3px;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.5);
}

.preloader__statbar {
  width: 100%;
  height: 3px;
  margin-top: 10px;
  background: rgba(255, 255, 255, 0.09);
}

.preloader__statbar i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #f5c518, #fff3c4);
  box-shadow: 0 0 8px rgba(245, 197, 24, 0.7);
  transform-origin: left;
}

@keyframes cardShine {
  to { transform: translateX(110%); }
}

@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.preloader__svc-head {
  position: absolute;
  left: 5%;
  top: 16%;
  z-index: 3;
  margin: 0;
  color: #f7f7f2;
  opacity: 0;
  transform: translateY(14px);
  animation: statementIn 0.75s ease 0.8s forwards;
}

.preloader__svc-head span {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px 7px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.95);
  color: #17120f;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: clamp(8px, 0.72vw, 10px);
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  clip-path: polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%);
}

.preloader__svc-head strong {
  display: block;
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: clamp(34px, 4.1vw, 66px);
  font-weight: 900;
  line-height: 0.96;
  text-transform: uppercase;
  text-shadow:
    0 0 18px rgba(255, 255, 255, 0.06),
    3px 3px 0 rgba(245, 197, 24, 0.2);
}

.preloader__svc-head mark {
  background: none;
  color: #f5c518;
  text-shadow:
    0 0 20px rgba(245, 197, 24, 0.22),
    3px 3px 0 rgba(245, 197, 24, 0.32);
}

.preloader__svc-list {
  position: absolute;
  right: 5%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: min(40vw, 520px);
}

.preloader__svccard {
  padding: 12px 18px 10px;
  gap: 3px;
  animation-fill-mode: backwards;
}

.preloader__svccard b {
  font-size: 15px;
  letter-spacing: 0.04em;
  white-space: normal;
  line-height: 1.1;
}

.preloader__svccard .preloader__statbar {
  margin-top: 6px;
}

.preloader__contact {
  position: absolute;
  right: 5%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: min(32vw, 400px);
}

.preloader__contactcard {
  padding: 20px 26px 18px;
  animation-fill-mode: backwards;
}

.preloader__contactcard b {
  font-size: 22px;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.preloader__contactcard input,
.preloader__contactcard select,
.preloader__contactcard textarea {
  width: 100%;
  margin-top: 8px;
  padding: 9px 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(245, 197, 24, 0.25);
  color: #f7f7f2;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  outline: none;
}

.preloader__contactcard input::placeholder,
.preloader__contactcard textarea::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.preloader__contactcard select:invalid {
  color: rgba(255, 255, 255, 0.35);
}

.preloader__contactcard select option {
  background: #17120f;
  color: #f7f7f2;
}

.preloader__contactcard textarea {
  resize: none;
}

.preloader__contactcard input:focus,
.preloader__contactcard select:focus,
.preloader__contactcard textarea:focus {
  border-color: rgba(245, 197, 24, 0.75);
  box-shadow: 0 0 12px rgba(245, 197, 24, 0.15);
}

.preloader__contactcard button {
  width: 100%;
  margin-top: 12px;
  padding: 11px;
  background: linear-gradient(90deg, #f5c518, #ffd84d);
  border: none;
  color: #17120f;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.34em;
  cursor: pointer;
}

.preloader__contactcard button:hover {
  box-shadow: 0 0 18px rgba(245, 197, 24, 0.45);
}

/* contact details under the heading */
.preloader__contact-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 24px 0 0;
  max-width: 330px;
}

.preloader__contact-details div {
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.14);
}

.preloader__contact-details dt {
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: rgba(245, 197, 24, 0.7);
}

.preloader__contact-details dd {
  margin: 4px 0 0;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.55;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.82);
}

.preloader__contact-details dd a {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px solid rgba(245, 197, 24, 0.5);
}

.preloader__contact-details dd a:hover {
  color: #f5c518;
}

/* social chips */
.preloader__socials {
  display: flex;
  gap: 8px;
  margin-top: 18px;
}

.preloader__socials a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(245, 197, 24, 0.45);
  color: rgba(245, 197, 24, 0.85);
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-decoration: none;
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
}

.preloader__socials a:hover {
  background: #f5c518;
  color: #17120f;
}

.preloader__svccard span {
  white-space: normal;
}

/* launch: everything except the frame + corner meta fades away */
.preloader.is-launched .preloader__tag,
.preloader.is-launched .preloader__hint,
.preloader.is-launched .preloader__launch-wrap {
  animation: launchOut 0.6s ease forwards;
  pointer-events: none;
}

@keyframes launchOut {
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(14px);
  }
}

@media (max-width: 1024px) {
  .preloader__statement {
    left: 6%;
    top: 18%;
    width: min(36vw, 340px);
  }

  .preloader__statement strong {
    font-size: 34px;
  }

  .preloader__statement p {
    max-width: 300px;
    font-size: 8px;
  }

  .preloader__brand {
    top: 64px;
    width: min(18vw, 130px);
  }

  /* more air between the wordmark, tagline and launch switch */
  .preloader__tag {
    top: calc(50% + 128px);
  }

  /* keep the accent line near the tagline, clear of the wordmark */
  .preloader__tag::before {
    top: -14px;
  }

  .preloader__launch-wrap {
    top: calc(50% + 196px);
  }

  .preloader__hint {
    bottom: 76px;
    width: 100%;
    text-align: center;
  }

  /* animated ticker sits centered below the hint on mobile/tablet */
  .preloader__meta--br {
    right: 50%;
    transform: translateX(50%);
    bottom: 48px;
    text-align: center;
  }
}

@media (max-width: 720px) {
  .preloader__copyright {
    bottom: 20px;
    width: calc(100% - 48px);
    font-size: 7px;
    line-height: 1.6;
    text-align: center;
  }

  .preloader__tag {
    top: calc(50% + 44px);
    font-size: 9px;
    letter-spacing: 0.24em;
  }

  .preloader__launch-wrap {
    top: calc(50% + 100px);
  }

  .preloader__brand {
    top: 84px;
    width: min(30vw, 130px);
  }

  .preloader__globe-head {
    top: 130px;
    bottom: auto;
    width: 88%;
    font-size: 8px;
    letter-spacing: 0.24em;
    white-space: normal;
    text-align: center;
    line-height: 2;
  }

  .preloader__statement {
    left: 50%;
    top: 162px;
    width: min(88vw, 360px);
    transform: translateX(-50%) translateY(12px);
    text-align: center;
  }

  .preloader__statement span {
    font-size: 10px;
    margin-bottom: 12px;
  }

  .preloader__statement strong {
    font-size: 34px;
  }

  .preloader__statement p {
    display: none;
  }

  .preloader__statement small {
    max-width: 300px;
    margin: 10px auto 0;
    padding-left: 0;
    border-left: none;
    text-align: center;
  }

  @keyframes statementIn {
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  .preloader__statline {
    display: none;
  }

  .preloader__statwrap--0,
  .preloader__statwrap--1,
  .preloader__statwrap--2 {
    left: 50%;
    right: auto;
    top: auto;
    transform: translateX(-50%);
    flex-direction: row;
  }

  .preloader__statwrap--0 { bottom: 262px; }
  .preloader__statwrap--1 { bottom: 190px; }
  .preloader__statwrap--2 { bottom: 118px; }

  .preloader__statcard {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px 12px;
    padding: 13px 20px 11px;
    width: min(78vw, 300px);
  }

  .preloader__statcard b {
    font-size: 22px;
  }

  .preloader__statcard em {
    display: none;
  }

  .preloader__statcard span {
    white-space: nowrap;
  }

  .preloader__statcard .preloader__statbar {
    flex-basis: 100%;
    width: 100%;
    margin-top: 4px;
  }

  .preloader__svc-head {
    top: 130px;
    left: 50%;
    width: 88vw;
    transform: translateX(-50%) translateY(14px);
    text-align: center;
  }

  .preloader__svc-head strong {
    font-size: 30px;
  }

  /* heading on one line on mobile */
  .preloader__svc-head strong br {
    display: none;
  }

  .preloader__svc-head strong mark {
    margin-left: 0.28em;
  }

  .preloader__svc-list {
    right: 50%;
    /* robot is 36vmin tall centered at 37%: start below its chin + bob */
    top: calc(37% + 18vmin + 28px);
    bottom: auto;
    transform: translateX(50%);
    width: min(78vw, 300px);
    gap: 8px;
    grid-template-columns: 1fr;
  }

  .preloader__svccard {
    width: auto;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    padding: 10px 14px 8px;
  }

  .preloader__svccard b {
    font-size: 12px;
  }

  .preloader__svccard span {
    display: none;
  }

  .preloader__contact {
    right: 50%;
    top: auto;
    bottom: 72px;
    transform: translateX(50%);
    width: min(80vw, 320px);
  }

  .preloader__contactcard {
    width: auto;
    flex-direction: column;
    align-items: stretch;
    padding: 10px 12px 8px;
  }

  .preloader__contactcard em {
    display: none;
  }

  .preloader__contactcard b {
    font-size: 14px;
    margin-bottom: 2px;
  }

  .preloader__contactcard input,
  .preloader__contactcard select,
  .preloader__contactcard textarea {
    margin-top: 5px;
    padding: 6px 10px;
    font-size: 9px;
  }

  .preloader__contactcard textarea {
    height: 44px;
  }

  .preloader__contactcard button {
    margin-top: 8px;
    padding: 8px;
  }

  .preloader__contact-details {
    margin: 12px auto 0;
    gap: 6px 12px;
    max-width: 88vw;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  /* phone + email share a row, address spans below */
  .preloader__contact-details div {
    width: calc(50% - 8px);
  }

  .preloader__contact-details div:last-child {
    width: 100%;
  }

  .preloader__contact-details dd {
    font-size: 9px;
  }

  .preloader__socials {
    margin-top: 12px;
    justify-content: center;
  }

}
</style>
